// Native sign-in for the iOS/iPadOS app (WKWebView hybrid).
//
// The native host runs the interactive flow (ASAuthorizationController for
// Apple, ASWebAuthenticationSession for Google — protocol §5 `auth.*`) and
// hands back tokens; this module completes the WEB Firebase session with
// signInWithCredential so the whole existing GraphQL/entitlement stack works
// unchanged. After web sign-in it mints a custom token from the existing
// POST /auth/custom-token endpoint (same exchange accountPortal/safariIAP use)
// and hands it to native (`auth.setSession`) so AppAuth.swift keeps a native
// Firebase session for StoreKit reconciliation.
//
// Only reachable when isNativeApp() — Chrome/Safari never import-execute these
// paths (AuthContext branches before calling).

import {
  GoogleAuthProvider,
  OAuthProvider,
  indexedDBLocalPersistence,
  setPersistence,
  signInWithCredential,
} from 'firebase/auth/web-extension';
import type { AuthCredential } from 'firebase/auth/web-extension';
import type { User } from 'firebase/auth';
import { auth } from '../config/firebaseConfig';
import { bridge, bridgeErrorCode } from '../utils/nativeApp';
import { log, warn, error as logError } from '../utils/logger';

export interface NativeAuthResult {
  user: {
    uid: string;
    displayName: string | null;
    email: string | null;
    photoURL: string | null;
    providerId: string;
  } | null;
  error?: string;
  /** True when the user dismissed the native sheet — treat as a no-op, no error UI. */
  cancelled?: boolean;
}

// Same ID-token → custom-token exchange as accountPortal.getCustomToken /
// safariIAP.mintCustomToken; null when signed out or the backend declines.
const mintCustomToken = async (user: User): Promise<string | null> => {
  try {
    const idToken = await user.getIdToken();
    const apiBase = (process.env.REACT_APP_GRAPHQL_API_URL || '').replace(/\/graphql$/, '');
    const res = await fetch(`${apiBase}/auth/custom-token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
    });
    if (!res.ok) return null;
    const data = (await res.json()) as { customToken?: string };
    return data.customToken ?? null;
  } catch {
    return null;
  }
};

/**
 * Hand the current web session to the native host (protocol `auth.setSession`)
 * so StoreKit verification has a native Firebase session. Best-effort: a failure
 * must never fail the web sign-in — purchase flows re-mint their own token.
 */
export const handoffSessionToNative = async (user: User): Promise<void> => {
  try {
    const customToken = await mintCustomToken(user);
    if (!customToken) {
      warn('[NativeAuth] No custom token minted — skipping native session handoff');
      return;
    }
    await bridge.call('auth.setSession', { customToken });
    log('[NativeAuth] Native session handoff complete');
  } catch (err) {
    warn('[NativeAuth] Native session handoff failed:', err);
  }
};

const completeSignIn = async (
  credential: AuthCredential,
  providerId: string,
): Promise<NativeAuthResult> => {
  await setPersistence(auth, indexedDBLocalPersistence);
  const result = await signInWithCredential(auth, credential);
  await handoffSessionToNative(result.user);
  return {
    user: {
      uid: result.user.uid,
      displayName: result.user.displayName || null,
      email: result.user.email || null,
      photoURL: result.user.photoURL || null,
      providerId,
    },
  };
};

const toFailure = (err: unknown, fallbackMsg: string): NativeAuthResult => {
  if (bridgeErrorCode(err) === 'CANCELLED') {
    return { user: null, cancelled: true };
  }
  logError('[NativeAuth]', fallbackMsg, err);
  return { user: null, error: err instanceof Error ? err.message : fallbackMsg };
};

/** Sign in with Apple via the native ASAuthorizationController (protocol §5). */
export const signInAppleNative = async (): Promise<NativeAuthResult> => {
  try {
    const { idToken, rawNonce } = await bridge.call<{
      idToken: string;
      rawNonce: string;
      email?: string;
      displayName?: string;
    }>('auth.signInApple');
    const credential = new OAuthProvider('apple.com').credential({ idToken, rawNonce });
    return await completeSignIn(credential, 'apple.com');
  } catch (err) {
    return toFailure(err, 'Apple sign-in failed');
  }
};

/** Sign in with Google via the native ASWebAuthenticationSession (protocol §5). */
export const signInGoogleNative = async (): Promise<NativeAuthResult> => {
  try {
    const { idToken } = await bridge.call<{ idToken: string }>('auth.signInGoogle');
    const credential = GoogleAuthProvider.credential(idToken);
    return await completeSignIn(credential, 'google.com');
  } catch (err) {
    return toFailure(err, 'Google sign-in failed');
  }
};

/** Clear the native Firebase session + App Group keys (protocol `auth.signOut`). */
export const signOutNative = async (): Promise<void> => {
  try {
    await bridge.call('auth.signOut');
  } catch (err) {
    warn('[NativeAuth] Native sign-out failed (web session cleared regardless):', err);
  }
};
